import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { FilterBar, type FilterDefinition } from '@/components/ui/FilterBar';
import { LiveRegion } from '@/components/ui/LiveRegion';
import { PageHeader } from '@/components/ui/PageHeader';
import { useToast } from '@/components/ui/useToast';
import { useDebounced } from '@/lib/useDebounced';

import { PageCards } from './PageCards';
import { TRANSITIONS, type MoveTarget, type PageActionId } from './pageActions';
import { bodyOf } from './pageForm';
import { LIFECYCLES, placementWord, stateWord, type PageRow } from './pageLabels';
import { refusalOf } from './pageRefusals';
import { PreviewDrawer } from './PreviewDrawer';
import { RemovePageDialog } from './RemovePageDialog';
import {
  PAGE_SIZE,
  usePageDetail,
  usePagesList,
  usePagesOrganizationId,
} from './usePages';
import { MOVE_FAILED, usePageWrites } from './usePageWrites';
import {
  usePagesParams,
  type PagesParams,
  type Placement,
  type Status,
} from './usePagesParams';
import { usePagesWritable } from './usePagesWritable';

const FILTERS: FilterDefinition[] = [
  {
    name: 'status',
    label: 'Status',
    // Removed is offered here and nowhere else: the list leaves it out (P8).
    options: LIFECYCLES.map((value) => ({ value, label: stateWord(value) })),
  },
  {
    name: 'placement',
    label: 'ARC Placement',
    options: [
      { value: 'in', label: placementWord(true) },
      { value: 'not_in', label: placementWord(false) },
    ],
  },
];

/** Whether a search or a filter narrows the list. */
function isFiltered(params: PagesParams): boolean {
  return params.search.trim() !== '' || params.status !== '' || params.placement !== '';
}

/**
 * The Custom Pages list (PB1, PB2, PB3, PB14): the filter bar, the cards and
 * the pager, over the server's paged list.
 *
 * The view lives in the address (P8): every change goes through
 * `usePagesParams`, and the list is keyed on what it reads back. Only the
 * search box keeps a draft of its own, settled into the address after a
 * pause, so a word is one request and the cards do not redraw per letter
 * (PNF1).
 */
export function PagesListPage() {
  const { writable, readOnlyReason } = usePagesWritable();
  const [params, change] = usePagesParams();

  const [draft, setDraft] = useState(params.search);
  const settled = useDebounced(draft, 300);
  useEffect(() => {
    if (settled !== params.search) change({ search: settled });
    // Only a settled draft writes the address; a changed address is read below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled]);
  // The address changed from outside - the back button, Clear - so the box follows.
  const [seenSearch, setSeenSearch] = useState(params.search);
  if (params.search !== seenSearch) {
    setSeenSearch(params.search);
    setDraft(params.search);
  }

  const navigate = useNavigate();
  const toast = useToast();
  const writes = usePageWrites();
  const { transition, rereadWritability } = writes;

  // A card action the person may not make: above the grid, until the next
  // action goes through or the view, or the organization in view, changes (P34).
  const organizationId = usePagesOrganizationId();
  const [refused, setRefused] = useState<string | null>(null);
  const viewKey = JSON.stringify([organizationId, params]);
  const [seenView, setSeenView] = useState(viewKey);
  if (viewKey !== seenView) {
    setSeenView(viewKey);
    setRefused(null);
  }

  const [removing, setRemoving] = useState<PageRow | null>(null);

  // A card's Preview reads the saved page, then the drawer posts its body.
  const [previewId, setPreviewId] = useState<string | null>(null);
  const previewed = usePageDetail(previewId);
  const previewBody = useMemo(
    () => (previewed.data ? bodyOf(previewed.data) : null),
    [previewed.data],
  );

  /**
   * One move from a card (B6, PB12): said in a toast when it goes through; a
   * 403 above the grid with writability reread (P34); a 404, a page this
   * organization no longer has, above the grid too; a 409 or 422 toasted in
   * the backend's words (P18, P21). The list is asked for again either way.
   */
  async function move(row: PageRow, to: MoveTarget) {
    try {
      await transition.mutateAsync({ id: row.id, to });
      setRefused(null);
      toast.show({ message: moved(row, to) });
    } catch (error) {
      const { kind, sentence } = refusalOf(error);
      // The session's and the organization context's: handled outside (PNF5).
      if (kind === 'outside') return;
      if (kind === 'notFound') setRefused(NOT_FOUND);
      else if (kind === 'forbidden') {
        setRefused(sentence);
        void rereadWritability();
      } else if (kind === 'conflict' || kind === 'invalid') {
        toast.show({ message: sentence, severity: 'error' });
      } else toast.show({ message: MOVE_FAILED, severity: 'error' });
    }
  }

  // The menu's handler is stable, so a keystroke in the search box does not
  // redraw the memoised cards (PNF1); it reads the latest render through a ref.
  const act = useRef<(action: PageActionId, row: PageRow) => void>(() => {});
  useEffect(() => {
    act.current = (action, row) => {
      if (action === 'edit') void navigate(ROUTES.pages.edit(row.id));
      else if (action === 'preview') setPreviewId(row.id);
      else if (action === 'remove') setRemoving(row);
      else {
        const to = TRANSITIONS[action];
        if (to) void move(row, to);
      }
    };
  });
  const onAction = useCallback(
    (action: PageActionId, row: PageRow) => act.current(action, row),
    [],
  );

  async function confirmRemove() {
    if (!removing) return;
    await move(removing, 'ARCHIVED');
    setRemoving(null);
  }

  const list = usePagesList(params);
  const rows = list.data?.rows ?? NO_ROWS;
  const total = list.data?.pagination.total ?? 0;
  const filtered = isFiltered(params);
  const settledReply = list.isSuccess && !list.isPlaceholderData;

  // A page past the end - its last card removed or moved out of the filter,
  // or an old link - comes back empty while the total says there are pages:
  // go to the last page there is, as Documents does.
  const pastTheEnd = settledReply && rows.length === 0 && total > 0 && params.page > 1;
  useEffect(() => {
    if (pastTheEnd) change({ page: Math.max(1, Math.ceil(total / PAGE_SIZE)) });
  }, [pastTheEnd, total, change]);

  // Stable, so a keystroke in the search box does not redraw the memoised
  // cards through a fresh callback (scalability rule 4).
  const { refetch } = list;
  const retry = useCallback(() => void refetch(), [refetch]);
  const turnPage = useCallback((page: number) => change({ page }), [change]);

  function clear() {
    setDraft('');
    change({ search: '', status: '', placement: '' });
  }

  const newPage = (testId: string) =>
    writable ? (
      <Button component={RouterLink} to={ROUTES.pages.new} data-testid={testId}>
        New Custom Page
      </Button>
    ) : null;

  let body;
  if (list.isError) {
    body = (
      <ErrorState title="The pages could not be loaded." onRetry={retry} testId="pages" />
    );
  } else if (settledReply && total === 0 && !filtered) {
    body = (
      <EmptyState
        title="No custom pages yet"
        description="Create a page and publish it to the app when it is ready."
        action={newPage('pages-new-first-button')}
        testId="pages"
      />
    );
  } else if (settledReply && total === 0) {
    body = (
      <EmptyState
        title="No pages match these filters."
        action={
          <Button
            variant="secondary"
            onClick={clear}
            data-testid="pages-no-match-clear-button"
          >
            Clear filters
          </Button>
        }
        testId="pages-no-match"
      />
    );
  } else {
    body = (
      <PageCards
        rows={rows}
        loading={list.isPending}
        page={params.page}
        total={total}
        onPageChange={turnPage}
        writable={writable}
        busy={writes.anyPending}
        onAction={onAction}
      />
    );
  }

  return (
    <Box data-testid="pages-page">
      <PageHeader
        title="Custom Pages"
        description="Create informational pages, keep them as drafts and publish them to the app."
        action={newPage('pages-new-button')}
      />

      {readOnlyReason ? (
        <Alert
          severity="warning"
          role="alert"
          data-testid="pages-read-only-alert"
          sx={{ mb: 3, maxWidth: 760 }}
        >
          {readOnlyReason}
        </Alert>
      ) : null}

      <FilterBar
        testId="pages-filters"
        searchValue={draft}
        onSearchChange={setDraft}
        searchPlaceholder="Search pages"
        filters={FILTERS}
        values={{ status: params.status, placement: params.placement }}
        onFilterChange={(name, value) =>
          change(
            name === 'status'
              ? { status: value as Status }
              : { placement: value as Placement },
          )
        }
        onClear={clear}
      />

      {refused ? (
        <Alert
          severity="error"
          data-testid="pages-error-alert"
          sx={{ mb: 3, maxWidth: 760 }}
        >
          {refused}
        </Alert>
      ) : null}

      {body}

      <LiveRegion
        message={settledReply ? `${total} ${total === 1 ? 'page' : 'pages'}` : ''}
      />

      <RemovePageDialog
        title={removing?.title ?? null}
        pending={transition.isPending}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoving(null)}
      />
      <PreviewDrawer
        open={previewId !== null}
        onClose={() => setPreviewId(null)}
        body={previewBody}
        read={{ failed: previewed.isError, retry: () => void previewed.refetch() }}
        request={writes.preview.mutateAsync}
      />
    </Box>
  );
}

/** A card's page that this organization no longer has (B13): above the grid. */
const NOT_FOUND =
  'That page was not found. It may have been removed, or it belongs to another organization.';

/** One empty array, so the cards' props do not change while loading. */
const NO_ROWS: PageRow[] = [];

/** What the toast says once a card's move is made. */
function moved(row: PageRow, to: MoveTarget): string {
  const title = `“${row.title}”`;
  if (to === 'ARCHIVED') return `${title} was removed.`;
  if (to === 'INACTIVE') return `${title} is unpublished.`;
  return `${title} is published.`;
}
